import type {BrowserObservationTime} from './browser-observation-clock'
import {
  CLIENT_INTERACTION_POLICY,
  type ClientInteraction,
  type ClientInteractionDependency,
  type ClientInteractionFact,
  type ClientInteractionObservation,
  type ClientInteractionReceipt,
  type ClientInteractionStart,
  type ClientInteractionUnknown,
} from './client-interaction-contract'

export const MAX_PENDING_CLIENT_INTERACTIONS = 256

interface Dependencies {
  now(): BrowserObservationTime
  nextSequence(): number
  visibility(): ClientInteractionFact['visibility']
  emit(fact: ClientInteractionFact): void
  reportError(error: Error, reason: 'observer' | 'clock' | 'emission' | 'observation_capacity'): void
}

export function createBrowserClientInteractionTracker(dependencies: Dependencies) {
  const pending = new Set<ClientInteraction>()
  const reported = new Set<string>()
  const report = (message: string, reason: Parameters<Dependencies['reportError']>[1] = 'observer') => {
    if (reported.has(message)) return
    reported.add(message)
    try {
      dependencies.reportError(new Error(message), reason)
    } catch {
      // eslint-disable-next-line no-console
      console.error('Unable to report client interaction telemetry error')
    }
  }
  const observe = <T>(operation: () => T): T | undefined => {
    try {
      return operation()
    } catch {
      report('Unable to observe client interaction telemetry')
      return undefined
    }
  }

  const begin = (input: ClientInteractionStart): ClientInteraction | undefined =>
    observe(() => {
      const started = dependencies.now()
      const {eventTimeStamp} = input
      const handoffRequired = input.handoff_scope !== 'not_applicable'
      const binding = (value: ClientInteractionReceipt): ClientInteractionReceipt => ({
        ...(value.session_id ? {session_id: value.session_id} : {}),
        ...(value.environment_id ? {environment_id: value.environment_id} : {}),
        ...(value.task_id ? {task_id: value.task_id} : {}),
        ...(value.chat_id ? {chat_id: value.chat_id} : {}),
        ...(value.submission_request_id ? {submission_request_id: value.submission_request_id} : {}),
        ...(value.turn_id ? {turn_id: value.turn_id} : {}),
        ...(value.pending_message_id ? {pending_message_id: value.pending_message_id} : {}),
      })
      const context = {
        client_interaction_id: input.client_interaction_id,
        client_view_id: input.client_view_id,
        interaction_kind: input.interaction_kind,
        surface: input.surface,
        role: input.role,
        ...(input.command_id ? {command_id: input.command_id} : {}),
        ...(input.command_revision !== undefined ? {command_revision: input.command_revision} : {}),
        handoff_scope: input.handoff_scope ?? 'single_action',
        ...binding(input),
      }
      // Epoch timestamps, future times and zero/unsupported values cannot
      // establish input queue delay on this document's monotonic clock.
      const verifiedStart =
        eventTimeStamp !== undefined &&
        Number.isFinite(eventTimeStamp) &&
        eventTimeStamp > 0 &&
        eventTimeStamp <= started.monotonicMs
      const inputStart = verifiedStart ? eventTimeStamp : started.monotonicMs
      const required = 'feedback|handoff' as const
      let committedEventId: string | undefined
      let feedbackEventId: string | undefined
      let handoffEventId: string | undefined
      let terminal = false
      let preparation: BrowserObservationTime | undefined
      let receipt: ClientInteractionReceipt = {}
      const waits = new Map<ClientInteractionDependency, BrowserObservationTime>()
      const unknowns = new Set<ClientInteractionUnknown>()
      let visibilityInterrupted = false

      const emit = (
        observation: ClientInteractionObservation,
        time: BrowserObservationTime = dependencies.now(),
      ): string | undefined => {
        const sequence = dependencies.nextSequence()
        if (
          !time.clockId ||
          time.clockId !== started.clockId ||
          !Number.isFinite(started.monotonicMs) ||
          started.monotonicMs < 0 ||
          !Number.isSafeInteger(sequence) ||
          sequence < 0 ||
          !Number.isFinite(time.observedAtMs) ||
          time.observedAtMs < 0 ||
          !Number.isFinite(time.monotonicMs) ||
          time.monotonicMs < started.monotonicMs ||
          !input.command_id ||
          !Number.isSafeInteger(input.command_revision) ||
          (input.command_revision ?? 0) < 1
        ) {
          report('Invalid client interaction identity, clock or sequence', 'clock')
          return
        }
        const eventId = `${time.clockId}/${sequence}`
        committedEventId ??= eventId
        try {
          dependencies.emit({
            ...CLIENT_INTERACTION_POLICY,
            ...context,
            ...receipt,
            event_id: eventId,
            committed_event_id: committedEventId,
            clock_id: time.clockId,
            sequence,
            observed_at_ms: time.observedAtMs,
            monotonic_ms: time.monotonicMs,
            input_start_ms: inputStart,
            input_time_source: verifiedStart ? 'event_timestamp' : 'handler_fallback',
            measurement_coverage: verifiedStart ? 'full_input_clock' : 'input_start_unverified',
            ...(verifiedStart ? {input_queue_ms: started.monotonicMs - inputStart} : {}),
            visibility: dependencies.visibility(),
            ...(visibilityInterrupted ? {visibility_interrupted: 'true' as const} : {}),
            required_obligations: required,
            ...observation,
          })
          return eventId
        } catch {
          report('Unable to emit client interaction observation', 'emission')
          return
        }
      }
      const elapsed = (time: BrowserObservationTime, start = inputStart) => {
        if (time.clockId !== started.clockId || !Number.isFinite(time.monotonicMs) || time.monotonicMs < start) {
          throw new Error('Invalid client interaction local duration')
        }
        return time.monotonicMs - start
      }
      const finish = () => {
        if (terminal || !feedbackEventId || (handoffRequired && !handoffEventId)) return
        if (!verifiedStart) {
          interaction.unresolved('input_start_unverified')
          return
        }
        if (
          emit({
            observation: 'completed',
            feedback_event_id: feedbackEventId,
            ...(handoffEventId ? {handoff_event_id: handoffEventId} : {}),
          })
        ) {
          terminal = true
          pending.delete(interaction)
        }
      }
      const interaction: ClientInteraction = {
        id: input.client_interaction_id,
        kind: input.interaction_kind,
        commandId: input.command_id,
        commandRevision: input.command_revision,
        started,
        preparationStarted: () =>
          observe(() => {
            if (terminal || preparation) return
            preparation = dependencies.now()
            emit({observation: 'preparation_started'}, preparation)
          }),
        preparationCompleted: () =>
          observe(() => {
            if (terminal || !preparation) return
            const time = dependencies.now()
            emit(
              {observation: 'preparation_completed', preparation_elapsed_ms: elapsed(time, preparation.monotonicMs)},
              time,
            )
            preparation = undefined
          }),
        dependencyChanged: (dependency, waiting) =>
          observe(() => {
            if (terminal) return
            if (waiting) {
              if (waits.has(dependency)) return
              const time = dependencies.now()
              waits.set(dependency, time)
              emit({observation: 'dependency_wait_started', dependency}, time)
            } else {
              const start = waits.get(dependency)
              if (!start) return
              waits.delete(dependency)
              const time = dependencies.now()
              emit(
                {
                  observation: 'dependency_wait_ended',
                  dependency,
                  dependency_wait_elapsed_ms: elapsed(time, start.monotonicMs),
                },
                time,
              )
            }
          }),
        feedback: method =>
          observe(() => {
            if (terminal || feedbackEventId) return
            if (dependencies.visibility() !== 'visible') {
              interaction.unresolved('hidden')
              return
            }
            const time = dependencies.now()
            feedbackEventId = emit(
              {
                observation: 'feedback_observed',
                feedback_method: method,
                feedback_coverage: 'semantic_visibility_not_paint',
                feedback_elapsed_ms: elapsed(time),
              },
              time,
            )
            finish()
          }),
        handoff: (actual, payloadMatches, sessionMatches) =>
          observe(() => {
            if (terminal || handoffEventId || !handoffRequired) return
            receipt = binding(actual)
            if (sessionMatches === undefined) return interaction.unresolved('session_unverified')
            if (payloadMatches === undefined) {
              return interaction.unresolved(
                input.interaction_kind === 'stop' ? 'stop_target_unverified' : 'payload_unverified',
              )
            }
            if (!sessionMatches) return interaction.failed('session_mismatch')
            if (!payloadMatches) return interaction.failed('payload_mismatch')
            const time = dependencies.now()
            handoffEventId = emit(
              {
                observation: 'transport_handoff',
                handoff_method: 'transport_send',
                payload_integrity: 'verified',
                session_integrity: 'verified',
                handoff_elapsed_ms: elapsed(time),
              },
              time,
            )
            finish()
          }),
        failed: reason =>
          observe(() => {
            if (terminal) return
            emit({observation: 'failed', reason})
            terminal = true
            pending.delete(interaction)
          }),
        unresolved: reason =>
          observe(() => {
            if (terminal || unknowns.has(reason)) return
            if (reason === 'hidden') visibilityInterrupted = true
            unknowns.add(reason)
            emit({observation: 'unresolved', reason})
            // Later positive transport evidence or a confirmed failure remains
            // reportable by the command's handle, even after losing UI observation.
            pending.delete(interaction)
          }),
      }
      pending.add(interaction)
      emit({observation: 'committed'}, started)
      if (pending.size > MAX_PENDING_CLIENT_INTERACTIONS) {
        pending.values().next().value?.unresolved('observation_capacity')
        report('Client interaction observation capacity exceeded', 'observation_capacity')
      }
      return interaction
    })

  return {
    begin,
    dispose(reason: 'reset' | 'disposed' | 'page_exit' = 'disposed') {
      for (const interaction of pending) interaction.unresolved(reason)
      pending.clear()
    },
  }
}
